import { useEffect } from 'react'
import type { PairedServer, ServerList } from './serverList'
import { MachineList } from './MachineList'
// Split out of StartAgentSheet.tsx in M6 (拆面板, 2026-09-03), moved verbatim —
// the sheet's four blocks were already four functions sharing nothing but the file.
//
// 2026-09-09: the "Server" heading and the collapsed "Add another machine" row
// left this file for the sheet's SUB-TABS (owner: 設計為 Sub Tab, 水平並排). This
// is now the picker alone; the tab above it is its name.
export function ServerField({
  list,
  picked,
  onPick,
  onClear,
}: {
  /**
   * The account's machines, fetched by the sheet (2026-09-13: it needs the same
   * answer to choose the default sub-tab, and one fetch serving two decisions beats
   * two fetches of one list). `null` until it lands.
   */
  list: ServerList | null
  picked: PairedServer | null
  onPick: (s: PairedServer) => void
  /** Deselect the machine and reopen the full list (the × on the collapsed row). */
  onClear?: (() => void) | undefined
}) {
  /*
   * Auto-select when there is exactly one machine. With a single server the picker is
   * a control with no choice in it, and leaving it unset would hide the whole rest of
   * the form behind a tap that cannot go any other way.
   *
   * MachineList below does its own fetch, and react-query is not in this path, so the
   * rows and this decision are separate requests. Kept rather than lifted further
   * because the alternative is MachineList reporting its list upward on every load,
   * which makes every other consumer carry a callback for one caller's convenience.
   */
  useEffect(() => {
    if (!picked && list?.kind === 'servers' && list.servers.length === 1) {
      onPick(list.servers[0]!)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, picked])

  return (
    <>
      {/*
        * ONE RENDERER FOR A MACHINE LIST (owner, 2026-09-02: 做).
        *
        * This was a `<select>` that re-implemented the five states MachineList already
        * keeps apart — its own doc comment said so, which is how the duplication was
        * found — while MachineList itself rendered nowhere and was a deletion candidate.
        * Promoting it kills the duplication in the direction that keeps the better
        * screen: the list names each machine's liveness, its agents, and why one is
        * blocked, none of which fits in a dropdown option.
        */}
      <MachineList onPick={onPick} selected={picked?.server_id ?? null} onClear={onClear} />
      {/*
        An offline machine stays SELECTABLE and says so. Create is a queued command:
        the cloud parks it and the machine collects it when it comes back, so refusing
        the choice would deny a thing that works. `unknown` is deliberately not called
        offline — that difference is "go fix the tunnel" versus "it is off, expected".
      */}
      {picked && picked.reachable !== 'online' && (
        <p className="gh-note">
          {picked.name} is {picked.reachable === 'offline' ? 'offline' : 'not reporting'}.
          The command waits for it; nothing is lost.
        </p>
      )}
    </>
  )
}
